// Notation compacte pour écrire les exercices dans le code.
//   C4:q        note do4 (do central), noire        r:h    silence, blanche
//   C#4:e/2     avec doigté 2                       [C3/5 E3/3 G3/1]:h   accord, doigtés par note
// Durées : w (ronde), h (blanche), q (noire), e (croche), s (double) ; un point prolonge de moitié.
// Les barres de mesure « | » sont ignorées : elles servent seulement à la lisibilité.

import { PPQ, type Hand, type Note, type Part } from './model';
import { parsePitch } from './pitchNames';

const DURATIONS: Record<string, number> = { w: 4, h: 2, q: 1, e: 0.5, s: 0.25 };

export function durationToTicks(token: string, ppq: number = PPQ): number {
  const m = /^([whqes])(\.?)$/.exec(token);
  if (!m) throw new Error(`durée illisible : "${token}"`);
  const beats = DURATIONS[m[1]] * (m[2] === '.' ? 1.5 : 1);
  return Math.round(beats * ppq);
}

function parseFinger(text: string | undefined): number | undefined {
  if (!text) return undefined;
  const f = Number(text);
  if (!Number.isInteger(f) || f < 1 || f > 5) throw new Error(`doigté invalide : "${text}"`);
  return f;
}

/** Construit une partie (une main) depuis la notation compacte. */
export function part(hand: Hand, text: string, options: { ppq?: number } = {}): Part {
  const ppq = options.ppq ?? PPQ;
  const notes: Note[] = [];
  let cursor = 0;
  let index = 0;
  const tokens = text.replace(/\|/g, ' ').match(/\[[^\]]*\]:[whqes]\.?(?:\/\d)?|\S+/g) ?? [];
  for (const token of tokens) {
    if (token.startsWith('[')) {
      const m = /^\[([^\]]+)\]:([whqes]\.?)$/.exec(token);
      if (!m) throw new Error(`accord illisible : "${token}"`);
      const ticks = durationToTicks(m[2], ppq);
      for (const item of m[1].split(/\s+/).filter(Boolean)) {
        const [p, f] = item.split('/');
        notes.push({ id: `${hand}-${index++}`, pitch: parsePitch(p), start: cursor, duration: ticks, hand, finger: parseFinger(f) });
      }
      cursor += ticks;
      continue;
    }
    const m = /^([A-Ga-g][#b]?-?\d|r):([whqes]\.?)(?:\/(\d))?$/.exec(token);
    if (!m) throw new Error(`élément illisible : "${token}"`);
    const ticks = durationToTicks(m[2], ppq);
    if (m[1] === 'r') {
      cursor += ticks;
      continue;
    }
    notes.push({ id: `${hand}-${index++}`, pitch: parsePitch(m[1]), start: cursor, duration: ticks, hand, finger: parseFinger(m[3]) });
    cursor += ticks;
  }
  return { hand, clef: hand === 'right' ? 'treble' : 'bass', notes };
}
